import React from "react";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import {
  ApiAssetItem,
  ApiAssetAllocation,
  ApiAssetCategory,
  ApiMonthlyAssetReview,
  assetApi,
} from "../services/api";
import { CHART_COLORS } from "../constants";
import { formatCurrency } from "../utils/helpers";
import { useAuth } from "../context/AuthContext";
import { useMonth } from "../context/MonthContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface AssetManagerProps {
  refreshTrigger?: number;
}

const today = () => new Date().toISOString().split("T")[0];

const dateInputValue = (date: string) => {
  if (!date) return today();
  return date.split("T")[0];
};

const monthLabel = (month: string) => {
  const [year, monthNumber] = month.split("-");
  return `${year}/${monthNumber}`;
};

const AssetManager: React.FC<AssetManagerProps> = ({ refreshTrigger }) => {
  const { user } = useAuth();
  const { selectedMonth, currentYear } = useMonth();
  const [categories, setCategories] = React.useState<ApiAssetCategory[]>([]);
  const [assets, setAssets] = React.useState<ApiAssetItem[]>([]);
  const [monthlyReviews, setMonthlyReviews] = React.useState<ApiMonthlyAssetReview[]>([]);
  const [allocation, setAllocation] = React.useState<ApiAssetAllocation[]>([]);
  const [totalAmount, setTotalAmount] = React.useState("0");
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  // Modal states
  const [showAddAssetModal, setShowAddAssetModal] = React.useState(false);
  const [editingAsset, setEditingAsset] = React.useState<ApiAssetItem | null>(null);
  const [editingCategory, setEditingCategory] = React.useState<ApiAssetCategory | null>(null);
  const [categoryName, setCategoryName] = React.useState("");
  const [showTransferModal, setShowTransferModal] = React.useState(false);

  // Add-asset form state (used by modal)
  const [assetForm, setAssetForm] = React.useState({
    category_id: "",
    name: "",
    amount: "",
    snapshot_date: today(),
  });

  // Edit-category form state (used by modal)
  const [editCategoryName, setEditCategoryName] = React.useState("");

  // Edit-asset form state (used by modal)
  const [editAssetForm, setEditAssetForm] = React.useState({
    category_id: "",
    name: "",
    amount: "",
    snapshot_date: today(),
  });

  // Transfer form state
  const [transferForm, setTransferForm] = React.useState({
    from_asset_item_id: "",
    to_asset_item_id: "",
    amount: "",
    snapshot_date: today(),
  });

  const yearMonth = `${currentYear}-${String(selectedMonth + 1).padStart(2, "0")}`;

  const loadAssets = React.useCallback(async () => {
    if (!user?.group_id) return;
    setLoading(true);
    setError("");
    try {
      const [categoryData, assetData, reviewData] = await Promise.all([
        assetApi.getCategories(user.group_id),
        assetApi.getAssets(user.group_id, yearMonth),
        assetApi.getMonthlyReview(user.group_id, `${currentYear}-01`, yearMonth),
      ]);
      setCategories(categoryData);
      setAssets(assetData.assets || []);
      setTotalAmount(assetData.total_amount || "0");
      setAllocation(assetData.allocation || []);
      setMonthlyReviews(reviewData.months || []);
      setAssetForm((prev) => ({
        ...prev,
        category_id: prev.category_id || categoryData[0]?.category_id || "",
      }));
    } catch (err) {
      console.error("Failed to load assets:", err);
      setError(err instanceof Error ? err.message : "资产数据加载失败");
    } finally {
      setLoading(false);
    }
  }, [user?.group_id, yearMonth, currentYear]);

  React.useEffect(() => {
    loadAssets();
  }, [loadAssets, refreshTrigger]);

  const resetAssetForm = () => {
    setAssetForm({
      category_id: categories[0]?.category_id || "",
      name: "",
      amount: "",
      snapshot_date: today(),
    });
  };

  const assetsByCategory = React.useMemo(() => {
    return categories.map((category) => ({
      category,
      assets: assets
        .filter((asset) => asset.category_id === category.category_id)
        .sort((a, b) => {
          const dateDiff = new Date(b.snapshot_date).getTime() - new Date(a.snapshot_date).getTime();
          if (dateDiff !== 0) return dateDiff;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }),
    }));
  }, [assets, categories]);

  const categoryNamesForChart = React.useMemo(() => {
    const names = new Set<string>();
    monthlyReviews.forEach((review) => {
      review.allocation.forEach((item) => names.add(item.category_name));
    });
    return Array.from(names);
  }, [monthlyReviews]);

  // Build a full 12-month grid; future months remain empty (no carry-forward)
  const fullYearReviews = React.useMemo(() => {
    const reviewMap = new Map<string, ApiMonthlyAssetReview>();
    monthlyReviews.forEach((review) => reviewMap.set(review.month, review));
    return Array.from({ length: 12 }, (_, i) => {
      const m = String(i + 1).padStart(2, "0");
      const monthKey = `${currentYear}-${m}`;
      return reviewMap.get(monthKey) || { month: monthKey, total_amount: "0", allocation: [] };
    });
  }, [monthlyReviews, currentYear]);

  const reviewChartData = React.useMemo(() => {
    return {
      labels: fullYearReviews.map((review) => monthLabel(review.month)),
      datasets: categoryNamesForChart.map((name, index) => ({
        label: name,
        data: fullYearReviews.map((review) => {
          const allocationItem = review.allocation.find((item) => item.category_name === name);
          return allocationItem ? Number(Number(allocationItem.percentage).toFixed(2)) : 0;
        }),
        backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
        stack: "asset-allocation",
      })),
    };
  }, [categoryNamesForChart, fullYearReviews]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: {
          callback: (value: string | number) => `${value}%`,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`,
        },
      },
    },
  };

  const handleAssetFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setAssetForm((prev) => ({ ...prev, [name]: value }));
  };

  // Add asset modal handlers
  const openAddAssetModal = () => {
    resetAssetForm();
    setShowAddAssetModal(true);
  };

  const closeAddAssetModal = () => {
    setShowAddAssetModal(false);
    resetAssetForm();
  };

  const handleSaveAsset = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user?.group_id) return;

    setSaving(true);
    setError("");
    try {
      const payload = {
        category_id: assetForm.category_id,
        name: assetForm.name.trim(),
        amount: assetForm.amount,
        snapshot_date: assetForm.snapshot_date,
      };
      await assetApi.createAsset(user.group_id, payload);
      closeAddAssetModal();
      await loadAssets();
    } catch (err) {
      console.error("Failed to save asset:", err);
      setError(err instanceof Error ? err.message : "资产保存失败");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAsset = async (asset: ApiAssetItem) => {
    if (!user?.group_id) return;
    setSaving(true);
    setError("");
    try {
      await assetApi.deleteAsset(user.group_id, asset.asset_item_id);
      await loadAssets();
    } catch (err) {
      console.error("Failed to delete asset:", err);
      setError(err instanceof Error ? err.message : "资产删除失败");
    } finally {
      setSaving(false);
    }
  };

  // Category handlers
  const handleCreateCategory = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user?.group_id || !categoryName.trim()) return;
    setSaving(true);
    setError("");
    try {
      await assetApi.createCategory(user.group_id, categoryName.trim());
      setCategoryName("");
      await loadAssets();
    } catch (err) {
      console.error("Failed to create asset category:", err);
      setError(err instanceof Error ? err.message : "类目保存失败");
    } finally {
      setSaving(false);
    }
  };

  const openEditCategoryModal = (category: ApiAssetCategory) => {
    setEditingCategory(category);
    setEditCategoryName(category.name);
  };

  const closeEditCategoryModal = () => {
    setEditingCategory(null);
    setEditCategoryName("");
  };

  // Edit asset modal handlers
  const openEditAssetModal = (asset: ApiAssetItem) => {
    setEditAssetForm({
      category_id: asset.category_id,
      name: asset.name || "",
      amount: asset.amount || "",
      snapshot_date: dateInputValue(asset.snapshot_date),
    });
    setEditingAsset(asset);
  };

  const closeEditAssetModal = () => {
    setEditingAsset(null);
    setEditAssetForm({ category_id: "", name: "", amount: "", snapshot_date: today() });
  };

  const handleSaveEditAsset = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user?.group_id || !editingAsset) return;
    setSaving(true);
    setError("");
    try {
      await assetApi.updateAsset(user.group_id, editingAsset.asset_item_id, {
        category_id: editAssetForm.category_id,
        name: editAssetForm.name.trim(),
        amount: editAssetForm.amount,
        snapshot_date: editAssetForm.snapshot_date,
      });
      closeEditAssetModal();
      await loadAssets();
    } catch (err) {
      console.error("Failed to update asset:", err);
      setError(err instanceof Error ? err.message : "资产更新失败");
    } finally {
      setSaving(false);
    }
  };

  const handleEditAssetFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setEditAssetForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEditCategory = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user?.group_id || !editingCategory || !editCategoryName.trim()) return;
    setSaving(true);
    setError("");
    try {
      await assetApi.updateCategory(user.group_id, editingCategory.category_id, editCategoryName.trim());
      closeEditCategoryModal();
      await loadAssets();
    } catch (err) {
      console.error("Failed to save asset category:", err);
      setError(err instanceof Error ? err.message : "类目保存失败");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category: ApiAssetCategory) => {
    if (!user?.group_id) return;
    setSaving(true);
    setError("");
    try {
      await assetApi.deleteCategory(user.group_id, category.category_id);
      await loadAssets();
    } catch (err) {
      console.error("Failed to delete asset category:", err);
      setError(err instanceof Error ? err.message : "类目删除失败");
    } finally {
      setSaving(false);
    }
  };

  // Transfer handlers
  const openTransferModal = () => {
    setTransferForm({
      from_asset_item_id: assets[0]?.asset_item_id || "",
      to_asset_item_id: assets[1]?.asset_item_id || assets[0]?.asset_item_id || "",
      amount: "",
      snapshot_date: today(),
    });
    setShowTransferModal(true);
  };

  const closeTransferModal = () => {
    setShowTransferModal(false);
  };

  const handleTransferFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setTransferForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTransfer = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user?.group_id) return;
    setSaving(true);
    setError("");
    try {
      await assetApi.transfer(user.group_id, {
        from_asset_item_id: transferForm.from_asset_item_id,
        to_asset_item_id: transferForm.to_asset_item_id,
        amount: transferForm.amount,
        snapshot_date: transferForm.snapshot_date,
      });
      closeTransferModal();
      await loadAssets();
    } catch (err) {
      console.error("Failed to transfer:", err);
      setError(err instanceof Error ? err.message : "转账失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-12">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-6 mb-6 bg-white border border-gray-200 rounded-xl shadow-lg p-6">
        <div className="lg:ml-6">
          <h2 className="text-2xl font-semibold">资产管理</h2>
          <div className="mt-8 text-3xl font-semibold text-slate-800">{formatCurrency(Number(totalAmount))}</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 w-full lg:w-auto">
          {allocation.map((item) => (
            <div key={item.category_id} className="p-3 shadow-sm bg-gray-100 p-4 rounded-lg">
              <div className="text-xs text-gray-500 truncate">{item.category_name}</div>
              <div className="text-lg font-semibold text-slate-800">{Number(item.percentage).toFixed(1)}%</div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="space-y-8">
          {/* Category management */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold">资产类目</h3>
            <form onSubmit={handleCreateCategory} className="flex gap-2">
              <input
                type="text"
                value={categoryName}
                onChange={(event) => setCategoryName(event.target.value)}
                placeholder="类目名称"
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                required
              />
              <button
                type="submit"
                disabled={saving}
                className="shrink-0 bg-slate-700 text-white font-semibold px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                新增
              </button>
            </form>
            <div className="divide-y divide-gray-100">
              {categories.map((category) => (
                <div key={category.category_id} className="flex items-center justify-between py-3">
                  <span className="text-sm font-medium text-gray-800">{category.name}</span>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => openEditCategoryModal(category)}
                      className="text-indigo-600 hover:text-indigo-900 transition-colors"
                      title="编辑"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteCategory(category)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                      title="删除"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">资产列表</h3>
              <div className="flex items-center gap-3">
                {loading && <span className="text-sm text-gray-500">加载中...</span>}
                <button
                  type="button"
                  onClick={openTransferModal}
                  disabled={assets.length < 2}
                  className="p-1.5 rounded-lg text-slate-600 hover:bg-gray-100 hover:text-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="转账"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={openAddAssetModal}
                  disabled={categories.length === 0}
                  className="p-1.5 rounded-lg text-slate-600 hover:bg-gray-100 hover:text-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="添加资产"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>
            {assets.length === 0 && !loading && (
              <div className="py-12 text-center text-sm text-gray-500">暂无资产数据</div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <colgroup>
                  <col className="w-[120px]" />
                  <col />
                  <col className="w-[140px]" />
                  <col className="w-[80px]" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="py-2 text-left text-xs font-medium text-gray-500">日期</th>
                    <th className="py-2 text-left text-xs font-medium text-gray-500">项目</th>
                    <th className="py-2 text-right text-xs font-medium text-gray-500">金额</th>
                    <th className="py-2 text-center text-xs font-medium text-gray-500">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {assetsByCategory.map(({ category, assets: categoryAssets }) => (
                    categoryAssets.length > 0 && (
                      <React.Fragment key={category.category_id}>
                        <tr>
                          <td colSpan={4} className="pt-4 pb-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-slate-800">{category.name}</h4>
                              <span className="text-sm text-gray-500">
                                {formatCurrency(categoryAssets.reduce((sum, asset) => sum + Number(asset.amount), 0))}
                              </span>
                            </div>
                          </td>
                        </tr>
                        {categoryAssets.map((asset) => (
                          <tr key={asset.asset_item_id}>
                            <td className="py-3 whitespace-nowrap text-sm text-gray-500">
                              {dateInputValue(asset.snapshot_date)}
                            </td>
                            <td className="py-3 text-sm font-medium text-gray-900 max-w-56 truncate">
                              {asset.name}
                            </td>
                            <td className="py-3 whitespace-nowrap text-sm text-right font-semibold text-slate-800">
                              {formatCurrency(Number(asset.amount))}
                            </td>
                            <td className="py-3 whitespace-nowrap text-center text-sm">
                              <div className="flex justify-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => openEditAssetModal(asset)}
                                  className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                  title="编辑"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAsset(asset)}
                                  className="text-red-600 hover:text-red-900 transition-colors"
                                  title="删除"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      {/* Yearly Review - full width */}
      <div className="mt-8 bg-white border border-gray-200 rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold mb-4">年度复盘</h3>
        {monthlyReviews.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500">暂无年度资产数据</div>
        ) : (
          <div className="relative h-80">
            <Bar data={reviewChartData} options={chartOptions} />
          </div>
        )}
      </div>

      {/* Add Asset Modal */}
      {showAddAssetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-800">新增资产</h2>
                <p className="text-gray-500 text-sm mt-1">添加一笔新的资产记录</p>
              </div>
              <button
                type="button"
                onClick={closeAddAssetModal}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSaveAsset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">类目</label>
                <select
                  name="category_id"
                  value={assetForm.category_id}
                  onChange={handleAssetFormChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                >
                  {categories.map((category) => (
                    <option key={category.category_id} value={category.category_id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">项目</label>
                <input
                  name="name"
                  type="text"
                  value={assetForm.name}
                  onChange={handleAssetFormChange}
                  placeholder="例如：招商银行活期"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">金额 (¥)</label>
                <input
                  name="amount"
                  type="number"
                  value={assetForm.amount}
                  onChange={handleAssetFormChange}
                  onWheel={(event) => event.currentTarget.blur()}
                  min="0"
                  step="0.01"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">日期</label>
                <input
                  name="snapshot_date"
                  type="date"
                  value={assetForm.snapshot_date}
                  onChange={handleAssetFormChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={saving || categories.length === 0}
                className="w-full bg-slate-700 text-white font-bold py-2 px-4 rounded-lg hover:bg-slate-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "保存中..." : "添加资产"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Asset Modal */}
      {editingAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-800">编辑资产</h2>
                <p className="text-gray-500 text-sm mt-1">修改资产记录</p>
              </div>
              <button
                type="button"
                onClick={closeEditAssetModal}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSaveEditAsset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">类目</label>
                <select
                  name="category_id"
                  value={editAssetForm.category_id}
                  onChange={handleEditAssetFormChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                >
                  {categories.map((category) => (
                    <option key={category.category_id} value={category.category_id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">项目</label>
                <input
                  name="name"
                  type="text"
                  value={editAssetForm.name}
                  onChange={handleEditAssetFormChange}
                  placeholder="例如：招商银行活期"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">金额 (¥)</label>
                <input
                  name="amount"
                  type="number"
                  value={editAssetForm.amount}
                  onChange={handleEditAssetFormChange}
                  onWheel={(event) => event.currentTarget.blur()}
                  min="0"
                  step="0.01"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">日期</label>
                <input
                  name="snapshot_date"
                  type="date"
                  value={editAssetForm.snapshot_date}
                  onChange={handleEditAssetFormChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-slate-700 text-white font-bold py-2 px-4 rounded-lg hover:bg-slate-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "保存中..." : "保存"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-800">资产转账</h2>
                <p className="text-gray-500 text-sm mt-1">在资产项目之间转账</p>
              </div>
              <button
                type="button"
                onClick={closeTransferModal}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleTransfer} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">转出资产</label>
                <select
                  name="from_asset_item_id"
                  value={transferForm.from_asset_item_id}
                  onChange={handleTransferFormChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                >
                  {assets.map((asset) => (
                    <option key={asset.asset_item_id} value={asset.asset_item_id}>
                      {asset.name} ({formatCurrency(Number(asset.amount))})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">转入资产</label>
                <select
                  name="to_asset_item_id"
                  value={transferForm.to_asset_item_id}
                  onChange={handleTransferFormChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                >
                  {assets.map((asset) => (
                    <option key={asset.asset_item_id} value={asset.asset_item_id}>
                      {asset.name} ({formatCurrency(Number(asset.amount))})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">转账金额 (¥)</label>
                <input
                  name="amount"
                  type="number"
                  value={transferForm.amount}
                  onChange={handleTransferFormChange}
                  onWheel={(event) => event.currentTarget.blur()}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">日期</label>
                <input
                  name="snapshot_date"
                  type="date"
                  value={transferForm.snapshot_date}
                  onChange={handleTransferFormChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={saving || !transferForm.from_asset_item_id || !transferForm.to_asset_item_id}
                className="w-full bg-slate-700 text-white font-bold py-2 px-4 rounded-lg hover:bg-slate-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "转账中..." : "确认转账"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-800">编辑类目</h2>
                <p className="text-gray-500 text-sm mt-1">修改资产类目名称</p>
              </div>
              <button
                type="button"
                onClick={closeEditCategoryModal}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSaveEditCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">类目名称</label>
                <input
                  type="text"
                  value={editCategoryName}
                  onChange={(event) => setEditCategoryName(event.target.value)}
                  placeholder="类目名称"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={saving || !editCategoryName.trim()}
                className="w-full bg-slate-700 text-white font-bold py-2 px-4 rounded-lg hover:bg-slate-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "保存中..." : "保存"}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default AssetManager;
